import type { ReactNode } from 'react';
import { create } from 'zustand';

export type ConfirmOptions = {
    /** Modal title */
    title: ReactNode;
    /** Modal content - can be string or ReactNode */
    content?: ReactNode;
    /** OK button text */
    okText?: string;
    /** Cancel button text */
    cancelText?: string;
    /** Use danger styling for OK button */
    okDanger?: boolean;
    /** Handler called when OK is clicked. Can be async. */
    onOk?: () => void | Promise<void>;
    /** Handler called when Cancel is clicked */
    onCancel?: () => void;
    /** Custom icon (defaults to CircleAlert) */
    icon?: ReactNode;
    /** Modal width */
    width?: number;
    /** Runs once the closing animation has finished, whichever button closed the modal */
    afterClose?: () => void;
    /** Which button takes keyboard focus when the modal opens (a permanent deletion focuses Cancel) */
    autoFocus?: 'ok' | 'cancel';
    /**
     * Called when another confirmation replaces this one while it is still open. The single
     * modal slot can show one request at a time; `confirmAction` uses this to settle the
     * displaced promise.
     */
    onReplaced?: () => void;
};

type ConfirmRequest = {
    /** Identity of one `show()` call; close and loading updates apply only to the matching request */
    id: number;
    options: ConfirmOptions;
};

type ConfirmModalState = {
    isOpen: boolean;
    isLoading: boolean;
    request: ConfirmRequest | null;
    /** Opens a confirmation and returns its request id. An open request is replaced and told so. */
    show: (options: ConfirmOptions) => number;
    /** Closes the modal; with an id, only when that request is still the one shown. */
    hide: (id?: number) => void;
    /** Sets the OK button's loading state, only when that request is still the one shown. */
    setIsLoading: (id: number, isLoading: boolean) => void;
};

let nextRequestId = 0;

export const useConfirmModalStore = create<ConfirmModalState>((set, get) => ({
    isOpen: false,
    isLoading: false,
    request: null,

    show(options) {
        const { request, isOpen } = get();
        if (request && isOpen) {
            request.options.onReplaced?.();
        }
        nextRequestId += 1;
        set({
            isOpen: true,
            isLoading: false,
            request: { id: nextRequestId, options },
        });
        return nextRequestId;
    },

    hide(id) {
        if (id !== undefined && get().request?.id !== id) {
            return;
        }
        set({
            isOpen: false,
            isLoading: false,
        });
    },

    setIsLoading(id, isLoading) {
        if (get().request?.id !== id) {
            return;
        }
        set({
            isLoading,
        });
    },
}));
